import { useState, useEffect, useRef } from 'react';
import { useMediaState } from 'modules/Canvas/contexts/MediaProvider/MediaState';
import { hasFrames } from 'components/ModelOutputRenderer/components/utils';
import { VideoFrameState } from 'components/Canvas/RegionThumb/common';

const useCapturedVideoFrame = (data: CF.API.TOutputData, currentSecond: number) => {
  const mediaState = useMediaState();
  const [frameVideo, setFrameVideo] = useState<VideoFrameState | null | undefined>();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const seekToNearestFrame = async (video: HTMLVideoElement, seekTime: number): Promise<HTMLVideoElement> => {
      await new Promise<void>((resolve, reject) => {
        video.addEventListener('seeked', () => resolve(), { once: true });
        video.addEventListener('error', (err) => reject(err));
        video.currentTime = seekTime;
      });

      return video;
    };

    const getSeekTime = (currentTime: number) => {
      if (hasFrames(data) && data.frames) {
        const FramesEntity = data.frames as CF.API.FramesEntity[];
        return FramesEntity[currentSecond].frame_info.time / 1000;
      } else {
        return currentTime;
      }
    };

    const drawFrame = async () => {
      if (mediaState?.imageData && 'video' in mediaState?.imageData) {
        const video = mediaState?.imageData.video;
        try {
          const tempVideo = videoRef.current;
          if (tempVideo) {
            const seekTime = getSeekTime(video.currentTime);
            const tempVideoFrame = await seekToNearestFrame(tempVideo, seekTime);
            // Update state with the captured frame and current time to force a re-render

            setFrameVideo({ updatedFrameTime: Date.now(), video: tempVideoFrame });
          }
        } catch (err) {
          console.error('Error capturing frame:', err);
        }
      }
    };

    if (!videoRef.current) {
      videoRef.current = document.createElement('video');
    }

    if (mediaState?.imageData && 'video' in mediaState?.imageData) {
      const video = mediaState.imageData.video;
      if (videoRef.current.src !== video.src) {
        videoRef.current.src = video.src;
        drawFrame();
      }
      video.addEventListener('timeupdate', drawFrame);
    }

    return () => {
      if (mediaState?.imageData && 'video' in mediaState?.imageData) {
        mediaState.imageData.video.removeEventListener('timeupdate', drawFrame);
      }
    };
  }, [mediaState?.imageData, data, currentSecond]);
  return frameVideo;
};

export default useCapturedVideoFrame;
